import { x } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { usePrefersReducedMotion } from '~/lib/a11y/usePrefersReducedMotion';
import { PlayButton } from '~/ui/buttons';
import { loadGifFreezeFrame } from './captureGifFreezeFrame';

type ImgProps = {
  src: string;
  alt: string;
  'aria-hidden'?: boolean;
  className?: string;
  [key: string]: unknown;
};

type Props = {
  url: string;
  alt: string;
  ariaHidden?: boolean;
  /** Start frozen (Cards6 / prefers-reduced-motion). */
  startPaused?: boolean;
  /**
   * When paused and freeze-frame capture fails, hide the media (WCAG 2.2.2
   * allows hide). Use for confirmed GIFs. Leave false for Cards6 fail-open so
   * static images still render if sniff/freeze cannot prove animation.
   */
  hideWhenFreezeUnavailable?: boolean;
  /** Fired once when freeze-frame capture fails (CORS / decode). */
  onFreezeUnavailable?: () => void;
  /** Rendered image element (keeps Image.tsx styled `ImageEl`). */
  renderImg: (props: ImgProps) => React.ReactNode;
};

/**
 * GIF play/pause for WCAG 2.2.2 (CAT-1236 / EL–E29).
 * Pauses by swapping to a static freeze frame.
 * Control matches video: round PlayButton centered over the media.
 */
export const GifPlayPauseImage = ({
  url,
  alt,
  ariaHidden,
  startPaused = false,
  hideWhenFreezeUnavailable = false,
  onFreezeUnavailable,
  renderImg
}: Props) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [playing, setPlaying] = useState(
    () => !(startPaused || prefersReducedMotion)
  );
  // undefined = loading, string = ready, null = failed
  const [freezeSrc, setFreezeSrc] = useState<string | null | undefined>(
    undefined
  );
  const onFreezeUnavailableRef = useRef(onFreezeUnavailable);
  onFreezeUnavailableRef.current = onFreezeUnavailable;

  useEffect(() => {
    let cancelled = false;
    setFreezeSrc(undefined);
    loadGifFreezeFrame(url).then(src => {
      if (cancelled) return;
      setFreezeSrc(src);
      if (src === null) onFreezeUnavailableRef.current?.();
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    setPlaying(!(startPaused || prefersReducedMotion));
  }, [prefersReducedMotion, startPaused, url]);

  const paused = !playing;
  const freezeLoading = freezeSrc === undefined;
  const freezeReady = typeof freezeSrc === 'string';
  const freezeFailed = freezeSrc === null;
  // Never show the live GIF while paused and freeze is still loading (or when
  // hide-on-failure is requested). Otherwise the control says "paused" while
  // the <img> keeps animating.
  const hideMedia =
    paused && (freezeLoading || (freezeFailed && hideWhenFreezeUnavailable));
  const showImage = !hideMedia;
  const displaySrc = paused && freezeReady ? freezeSrc : url;

  const label = playing ? t`Pause animation` : t`Play animation`;
  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setPlaying(p => !p);
  };

  return (
    <>
      {showImage ? (
        renderImg({
          src: displaySrc,
          alt: alt || '',
          'aria-hidden': ariaHidden
        })
      ) : (
        <x.div
          w="100%"
          h="100%"
          minH="80px"
          backgroundColor="greyLight"
          borderRadius="inherit"
          aria-hidden
        />
      )}
      {/* Same overlay pattern as Video.tsx — circular control, centered. */}
      <x.div
        position="absolute"
        top={0}
        left={0}
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ zIndex: 2 }}
        pointerEvents="none"
      >
        <PlayButton
          type="button"
          state={playing ? 'playing' : 'paused'}
          aria-label={label}
          title={label}
          aria-pressed={playing}
          onClick={toggle}
          style={{ pointerEvents: 'auto' }}
        />
      </x.div>
    </>
  );
};
